/**
 * Money module — read-side derivation over REAL data (foundation slice).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS (and is NOT)
 * ─────────────────────────────────────────────────────────────────────────
 *  The /money Payments tab must read LIVE Firestore (`projects/` via
 *  `listenProjects`) — NOT the demo seed. This module is the PURE
 *  derivation that turns a live `LiveProject[]` into:
 *    • per-project money rows (value / received / balance + the gate list),
 *    • per-firm splits (Aayatan Veda vs RKH HUF — the locked routing rule),
 *    • multi-currency overview (₹ and $ kept SEPARATE, never summed),
 *    • honest empty states (a number that cannot yet be computed is null /
 *      shown as an empty state — NO fabricated demo figures).
 *
 *  The money FIGURES still resolve from the LOCKED engagementTemplate gates
 *  (one pricing path — same `moneyViewOf` contract the Admin demo used), so
 *  there is no second pricing authority. The SOLE release authority remains
 *  `recheckGate` / `projects/{id}.gateState.moneyPaid` (server) — this is a
 *  read/visualise surface; it neither gates nor records payments. Recording
 *  a payment (writing a `payments` ledger entry + flipping a gate) is the
 *  NEXT slice (record-payment).
 *
 *  CURRENCY (foundation): a project's billing currency comes from
 *  `project.currency` when present; until the record-payment / quote slice
 *  stamps it, a project with no currency is treated as INR (the default for
 *  every domestic engagement). USD projects surface in the $ column.
 *
 *  FIRM ROUTING (foundation): a project's firm comes from `project.firmId`
 *  when an admin has set it; otherwise it is AUTO-derived from
 *  `firmFor({currency, gst})` (USD/GST → Aayatan Veda · INR-no-GST → RKH
 *  HUF). `gst` defaults true for INR (domestic AV work is GST-billed) —
 *  the per-project override that flips a job to the HUF is a record-payment
 *  / invoice-slice control; here we expose the AUTO result + whether it was
 *  explicitly overridden.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * PURE — no Firebase, no React. The Node test runner exercises every
 * derivation directly with hand-built `LiveProject` rows.
 */
import {
  getEngagementTemplate,
  resolveGateAmount,
  type EngagementTemplate,
  type Tier,
} from './engagementTemplate';
import { effectiveClearedGateIds, type LiveProject } from './projectStore';
import {
  firmFor,
  getFirm,
  type Currency,
  type FirmId,
} from './firms';

/** The billing-relevant fields the money module reads off a project. */
export interface MoneyProject {
  pid: string;
  vp: string;
  projectName: string;
  clientName: string;
  clientNo: string;
  clientId: string;
  tierCode: Tier;
  valueINR: number;
  /** Money-gate ids already cleared (drives received vs balance). */
  clearedGateIds: string[];
  /** Billing currency — defaults to INR when the project has none yet. */
  currency: Currency;
  /** Explicit firm override (admin-set); null → auto-route. */
  firmIdOverride: FirmId | null;
  /** Whether this engagement is GST-billed (drives firm auto-route + tax). */
  gst: boolean;
  /**
   * AGREED-PRICE FIX (2026-06-15) — per-gate expected amounts in the
   * ENGAGEMENT's own currency, set from the agreed price on a live project
   * (the $40 fix). When a gate id is present here, this amount WINS over the
   * template `amountINR` / %-split. Empty → template defaults (the
   * un-negotiated standing-INR path). Closes the "$11,000" bug — a USD
   * engagement priced at $40 expects $40, not the ₹11,000 template figure
   * wearing a "$" sign.
   */
  gateAmounts: Record<string, number>;
}

/**
 * Adapt a `LiveProject` into a `MoneyProject`. The currency / firm / gst
 * fields are read defensively off the project doc (they may not exist until
 * the quote/record-payment slices stamp them) with the locked defaults:
 * INR currency, GST true for INR domestic work, firm auto-routed.
 *
 * `raw` is the underlying project doc when available (carries `currency` /
 * `firmId` / `gst` that are not on the typed `LiveProject` yet); when only
 * the typed row is available, the defaults apply.
 */
export function moneyProjectFromLive(
  p: LiveProject,
  raw?: Record<string, unknown>,
): MoneyProject {
  // Prefer the typed money fields now surfaced on LiveProject (slice 2);
  // fall back to a raw doc when one is passed (back-compat for older callers).
  const currency: Currency =
    p.currency === 'USD' || (raw && raw.currency === 'USD') ? 'USD' : 'INR';
  const firmIdOverride: FirmId | null =
    p.firmId === 'firm1' || p.firmId === 'firm2'
      ? p.firmId
      : raw && (raw.firmId === 'firm1' || raw.firmId === 'firm2')
        ? (raw.firmId as FirmId)
        : null;
  // GST default: USD export work is LUT/no-IGST; INR domestic AV work is
  // GST-billed unless explicitly flipped to the HUF (gst:false on the doc).
  const gstRaw: boolean | null =
    typeof p.gst === 'boolean'
      ? p.gst
      : raw && typeof raw.gst === 'boolean'
        ? (raw.gst as boolean)
        : null;
  const gst: boolean = gstRaw != null ? gstRaw : currency === 'USD' ? false : true;
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    clientId: p.clientId,
    tierCode: p.tierCode,
    valueINR: p.valueINR,
    // BUG A FIX (2026-06-16) — derive cleared from the CANONICAL source:
    // `moneyPaid` (the authority `markPaidManual` flips) ∪ the legacy
    // `clearedGateIds`. Reading only `clearedGateIds` here made a recorded
    // $40 payment show "Received $0" while the modal showed "$40 collected".
    clearedGateIds: effectiveClearedGateIds(p),
    // AGREED-PRICE FIX — per-gate amounts set from the agreed price (the $40
    // fix). Prefer the typed field; fall back to a raw doc for older callers.
    gateAmounts:
      p.gateAmounts && typeof p.gateAmounts === 'object'
        ? p.gateAmounts
        : raw && raw.gateAmounts && typeof raw.gateAmounts === 'object'
          ? (raw.gateAmounts as Record<string, number>)
          : {},
    currency,
    firmIdOverride,
    gst,
  };
}

function templateOf(p: MoneyProject): EngagementTemplate | null {
  return getEngagementTemplate(p.tierCode) ?? null;
}

/** The resolved firm for a project: override wins, else the locked auto-route. */
export function firmOf(p: MoneyProject): {
  firmId: FirmId;
  overridden: boolean;
} {
  if (p.firmIdOverride) return { firmId: p.firmIdOverride, overridden: true };
  return {
    firmId: firmFor({ currency: p.currency, gst: p.gst }),
    overridden: false,
  };
}

export interface MoneyGateRow {
  id: string;
  label: string;
  amountINR: number;
  cleared: boolean;
}

export interface ProjectMoney {
  pid: string;
  vp: string;
  projectName: string;
  clientName: string;
  clientNo: string;
  tierCode: Tier;
  currency: Currency;
  firmId: FirmId;
  firmShort: string;
  firmOverridden: boolean;
  /** Total engagement value (in the project's currency units; INR figure). */
  valueINR: number;
  receivedINR: number;
  balanceINR: number;
  gates: MoneyGateRow[];
  /**
   * True when the tier has NO priced money gates we can resolve (a legacy
   * import with no tier value, or a tier with no money gate) — the UI shows
   * an honest "not yet priced" state instead of ₹0.
   */
  unpriced: boolean;
}

/**
 * Resolve ONE project's money picture from its LOCKED engagementTemplate
 * gates (amounts are NOT invented — fixed gates use `amountINR`, %-split
 * gates resolve against the project value). Mirrors the demo `moneyViewOf`
 * contract exactly (one pricing path) but over a real project + with the
 * firm/currency resolution layered on.
 */
export function projectMoney(p: MoneyProject): ProjectMoney {
  const tpl = templateOf(p);
  const { firmId, overridden } = firmOf(p);
  const firm = getFirm(firmId);

  const gates: MoneyGateRow[] = tpl
    ? tpl.gates
        .filter(
          (g): g is Extract<typeof g, { kind: 'money' }> => g.kind === 'money',
        )
        .map((g) => {
          // THE ONE CANONICAL RESOLVER — agreed `gateAmounts` override wins
          // over the template default (the $40 fix). Every consumer (this
          // summary, the project page, the Record-a-payment modal) calls the
          // SAME `resolveGateAmount`, so they can never disagree again.
          const amount = resolveGateAmount(g, p.valueINR, p.gateAmounts);
          return {
            id: g.id,
            label: g.label,
            amountINR: amount,
            cleared: p.clearedGateIds.includes(g.id),
          };
        })
    : [];

  const totalGated = gates.reduce((s, g) => s + g.amountINR, 0);
  const receivedINR = gates
    .filter((g) => g.cleared)
    .reduce((s, g) => s + g.amountINR, 0);
  // A tier with no resolvable priced gates, or a project with value 0 and
  // only %-split gates, is "unpriced" — the honest empty state.
  const unpriced = gates.length === 0 || (totalGated === 0 && p.valueINR === 0);

  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tierCode: p.tierCode,
    currency: p.currency,
    firmId,
    firmShort: firm.shortName,
    firmOverridden: overridden,
    valueINR: p.valueINR,
    receivedINR,
    balanceINR: Math.max(0, totalGated - receivedINR),
    gates,
    unpriced,
  };
}

/** A money total in ONE currency (₹ and $ are NEVER summed together). */
export interface CurrencyTotal {
  currency: Currency;
  value: number;
  received: number;
  balance: number;
}

/** Per-firm money split, each carrying its own per-currency totals. */
export interface FirmMoneySplit {
  firmId: FirmId;
  firmShort: string;
  projectCount: number;
  /** Totals keyed by currency — INR and USD kept separate. */
  byCurrency: CurrencyTotal[];
}

export interface MoneyOverview {
  projectCount: number;
  /** How many priced (resolvable) projects fed the figures. */
  pricedCount: number;
  /** How many are not yet priced (shown as an honest count, not as ₹0). */
  unpricedCount: number;
  /** Cross-firm per-currency totals (₹ and $ separate). */
  byCurrency: CurrencyTotal[];
  /** Per-firm split (Aayatan Veda · RKH HUF). */
  byFirm: FirmMoneySplit[];
  /** Every project's resolved money row (for the per-project list). */
  rows: ProjectMoney[];
}

function emptyCurrencyTotal(currency: Currency): CurrencyTotal {
  return { currency, value: 0, received: 0, balance: 0 };
}

function addInto(map: Map<Currency, CurrencyTotal>, row: ProjectMoney): void {
  const t = map.get(row.currency) ?? emptyCurrencyTotal(row.currency);
  t.value += row.valueINR;
  t.received += row.receivedINR;
  t.balance += row.balanceINR;
  map.set(row.currency, t);
}

/** Stable currency ordering for display (₹ first, then $). */
function sortCurrency(a: CurrencyTotal, b: CurrencyTotal): number {
  if (a.currency === b.currency) return 0;
  return a.currency === 'INR' ? -1 : 1;
}

/**
 * Build the full money overview from the live project rows. Cross-firm and
 * per-firm totals are kept PER-CURRENCY so ₹ and $ are never conflated.
 * `unpriced` rows are counted but contribute ₹0 (they show as an honest
 * "not yet priced" count in the UI, not as fabricated value).
 */
export function moneyOverview(projects: MoneyProject[]): MoneyOverview {
  const rows = projects.map(projectMoney);

  const crossCurrency = new Map<Currency, CurrencyTotal>();
  const firmMap = new Map<
    FirmId,
    { firmShort: string; count: number; byCurrency: Map<Currency, CurrencyTotal> }
  >();

  let pricedCount = 0;
  for (const row of rows) {
    if (!row.unpriced) pricedCount += 1;
    addInto(crossCurrency, row);
    const fm =
      firmMap.get(row.firmId) ??
      {
        firmShort: row.firmShort,
        count: 0,
        byCurrency: new Map<Currency, CurrencyTotal>(),
      };
    fm.count += 1;
    addInto(fm.byCurrency, row);
    firmMap.set(row.firmId, fm);
  }

  const byFirm: FirmMoneySplit[] = [...firmMap.entries()]
    .map(([firmId, fm]) => ({
      firmId,
      firmShort: fm.firmShort,
      projectCount: fm.count,
      byCurrency: [...fm.byCurrency.values()].sort(sortCurrency),
    }))
    // Aayatan Veda (firm1) first.
    .sort((a, b) => (a.firmId < b.firmId ? -1 : 1));

  return {
    projectCount: rows.length,
    pricedCount,
    unpricedCount: rows.length - pricedCount,
    byCurrency: [...crossCurrency.values()].sort(sortCurrency),
    byFirm,
    rows,
  };
}

/** ₹ formatter — Indian grouping, no decimals. */
export function inr(n: number): string {
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/** $ formatter — US grouping, no decimals. */
export function usd(n: number): string {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Format an amount in the given currency (₹ / $). */
export function money(n: number, currency: Currency): string {
  return currency === 'USD' ? usd(n) : inr(n);
}

/**
 * PURE — filter the ALREADY-TRACKED engagement rows by a free-text needle
 * (VP code · client name · C-number · project name). This is DISTINCT from the
 * "Add an engagement" picker search, which searches UNtracked projects; this
 * one filters the tracked list the owner already built so a long list stays
 * navigable (2026-06-16 scale fix). Empty needle → the list unchanged.
 */
export function searchTrackedEngagements(
  rows: ProjectMoney[],
  q: string,
): ProjectMoney[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((m) =>
    `${m.vp} ${m.clientName} ${m.clientNo} ${m.projectName}`
      .toLowerCase()
      .includes(needle),
  );
}
